import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';

import MapPinIcon from '../../../assets/svgComponents/BrowseMapIcon';
import LocationIcon from '../../../assets/svgComponents/LocationIcon';
import Notification from '../../Notification/Notification';
import CustomTextField from '../CustomTextField/CustomTextField';
import { TextInput } from '../TextInput/TextInput';

import style from './LocationSelect.module.scss';

const LocationSelect = ({
  value,
  required,
  onChange,
  linkedInImport,
  reverse,
  placeholder = '',
  disableFloatingLabel = false,
  isJobLocationSearch = false,
  organizationLocations = [],
  isApplicationFlow = false,
  isSignupFlow = false,
}) => {
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();

  const searchOptions = useMemo(() => ({ types: ['(regions)'] }), []);
  const [address, setAddress] = useState(JSON.parse(searchParams.get('location'))?.full_address || '');
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isSelectionMade, setIsSelectionMade] = useState(false);
  const [isLocationFetching, setIsLocationFetching] = useState(false);
  const [hasUserClearedLocation, setHasUserClearedLocation] = useState(false);
  const [hasAttemptedGeolocation, setHasAttemptedGeolocation] = useState(false);

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      Notification.warning(t('location.geolocationNotSupported'));
      setHasAttemptedGeolocation(true);
      return;
    }

    if (typeof window.google === 'undefined' || !window.google.maps) {
      Notification.warning(t('location.googleMapsNotAvailable'));
      setHasAttemptedGeolocation(true);
      return;
    }

    setHasAttemptedGeolocation(true);
    setIsLocationFetching(true);

    const geolocationOptions = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 300000,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        if (
          typeof latitude !== 'number' ||
          typeof longitude !== 'number' ||
          Number.isNaN(latitude) ||
          Number.isNaN(longitude)
        ) {
          setIsLocationFetching(false);
          Notification.error(t('location.invalidCoordinates'));
          return;
        }

        const geocoder = new window.google.maps.Geocoder();
        const location = {};
        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          setIsLocationFetching(false);

          if (status === 'OK' && results && results.length > 0) {
            results.forEach((comp) => {
              if (comp.types.includes('country')) {
                location.countryCode = comp.address_components[0].short_name;
                location.country = comp.address_components[0].long_name;
              } else if (comp.types.includes('administrative_area_level_1')) {
                location.stateCode = comp.address_components[0].short_name;
                location.state = comp.address_components[0].long_name;
              } else if (comp.types.includes('locality')) {
                location.city = comp.address_components[0].long_name;
              } else if (!location.city && comp.types.includes('administrative_area_level_2')) {
                location.city = comp.address_components[0].long_name;
              }
            });
            location.full_address = [location.city, location.stateCode, location.country].filter((i) => i).join(', ');
            location.lat = latitude;
            location.lon = longitude;

            setAddress(location.full_address);
            onChange(location);
            setHasUserClearedLocation(false);
          } else {
            Notification.error(t('notifications.unableToGetLocation'));
          }
        });
      },
      (error) => {
        setIsLocationFetching(false);

        if (error.code === error.TIMEOUT) {
          Notification.error(t('location.requestTimeout'));
        }
      },
      geolocationOptions
    );
  };

  useEffect(() => {
    if (!searchParams.size) {
      setAddress('');
      setHasUserClearedLocation(true);
    }
  }, [searchParams]);

  const handleChange = (newAddress) => {
    setIsSelectionMade(false);
    if (!isEmpty(organizationLocations)) {
      const filtered = organizationLocations.filter((loc) =>
        loc?.full_address?.toLowerCase().includes(newAddress.toLowerCase())
      );
      setFilteredLocations(filtered);
    }
    setAddress(newAddress);
    if (!newAddress) {
      setFilteredLocations([]);
      onChange(null);
      setHasUserClearedLocation(true);
    }
  };
  const handleSelect = (selectedAddress) => {
    setIsSelectionMade(true);
    setAddress(selectedAddress);
    if (!selectedAddress) {
      return onChange(null);
    }
    setHasUserClearedLocation(false);
    const location = {};
    const matchedJobLocation = organizationLocations.find((loc) => loc?.full_address === selectedAddress);
    if (matchedJobLocation) {
      location.place_id = matchedJobLocation.place_id;
      location.full_address = matchedJobLocation?.full_address;
      location.zip_code = matchedJobLocation?.zip_code;
      location.country_name = matchedJobLocation?.country_name;
      location.state = matchedJobLocation?.state;
      location.city = matchedJobLocation?.city;
      location.lat = matchedJobLocation?.lat;
      location.lon = matchedJobLocation?.lon;
      setAddress(matchedJobLocation?.full_address);
      setFilteredLocations([]);
      onChange(location);
    } else {
      geocodeByAddress(selectedAddress)
        .then((results) => {
          location.place_id = results[0].place_id;
          location.full_address = selectedAddress;
          results[0].address_components.forEach((comp) => {
            if (comp.types.includes('postal_code')) {
              location.zip_code = comp.short_name;
            } else if (comp.types.includes('country')) {
              location.country_name = comp.long_name;
              location.country_iso_code = comp.short_name;
            } else if (comp.types.includes('administrative_area_level_1')) {
              location.state = comp.short_name;
            } else if (comp.types.includes('locality')) {
              location.city = comp.long_name;
            } else if (!location.city && comp.types.includes('administrative_area_level_2')) {
              location.city = comp.long_name;
            }
          });
          return getLatLng(results[0]);
        })
        .then((latLng) => {
          location.lat = latLng.lat;
          location.lon = latLng.lng;
          onChange(location);
        })
        .catch(() => {});
    }
  };

  useEffect(() => {
    if (value?.full_address && !address && !linkedInImport) {
      handleSelect(value.full_address);
      setHasUserClearedLocation(false);
    }
    if (value?.full_address && linkedInImport) {
      handleSelect(value.full_address);
      setHasUserClearedLocation(false);
    }
  }, [value]);

  useEffect(() => {
    if (
      isSignupFlow &&
      !value?.full_address &&
      !address &&
      !isLocationFetching &&
      !hasUserClearedLocation &&
      !hasAttemptedGeolocation
    ) {
      const isGoogleMapsAvailable = () =>
        typeof window.google !== 'undefined' &&
        window.google &&
        window.google.maps &&
        typeof window.google.maps.Geocoder === 'function';

      if (isGoogleMapsAvailable()) {
        getUserLocation();
      } else {
        let attempts = 0;
        const maxAttempts = 50;

        const checkGoogleMaps = () => {
          attempts += 1;
          if (isGoogleMapsAvailable()) {
            getUserLocation();
          } else if (attempts < maxAttempts) {
            setTimeout(checkGoogleMaps, 100);
          } else {
            setIsLocationFetching(false);
            setHasAttemptedGeolocation(true);
            Notification.error(t('location.apiFailedToLoad'));
          }
        };
        checkGoogleMaps();
      }
    }
  }, [isSignupFlow, value, address, isLocationFetching, hasUserClearedLocation, hasAttemptedGeolocation]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const container = document.querySelector(`.${style.locationSelectContainer}`);
      if (container && !container.contains(event.target)) {
        setIsSelectionMade(true);
        setFilteredLocations([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <PlacesAutocomplete value={address} onChange={handleChange} onSelect={handleSelect} searchOptions={searchOptions}>
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div
          className={classNames(style.locationSelectContainer, {
            [style.LocationFilterContainer]: disableFloatingLabel,
          })}
        >
          {isJobLocationSearch ? (
            <input
              className={style.inputField}
              {...getInputProps()}
              type="text"
              placeholder={t('placeholders.location')}
            />
          ) : null}
          {(isApplicationFlow || isSignupFlow) && (
            <div className="w-full">
              <TextInput {...getInputProps()} placeholder={placeholder} />
            </div>
          )}
          {!isJobLocationSearch && !isApplicationFlow && !isSignupFlow ? (
            <CustomTextField
              label={required ? t('location.locationRequired') : t('location.title')}
              {...getInputProps()}
              CustomIcon={disableFloatingLabel ? MapPinIcon : LocationIcon}
              reverseIcon={reverse}
              placeholder={placeholder}
              disableFloatingLabel={disableFloatingLabel}
              isJobLocationSearch={isJobLocationSearch}
            />
          ) : null}
          {(loading || isLocationFetching) && (
            <div
              className={classNames(style.loading, {
                [style.LocationOptionContainer]: disableFloatingLabel,
              })}
            >
              <span className={style.loadingText}>
                {isLocationFetching ? t('location.gettingLocation') : t('location.loading')}
              </span>
            </div>
          )}
          {(filteredLocations.length > 0 || suggestions.length > 0) &&
            !loading &&
            !isLocationFetching &&
            !isSelectionMade && (
              <div
                className={classNames(style.suggestionsCont, {
                  [style.LocationOptionContainer]: disableFloatingLabel,
                })}
                onClick={(e) => {
                  if (e.target.closest(`.${style.filteredLocationItem}`)) {
                    setIsSelectionMade(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setIsSelectionMade(true);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {filteredLocations.map((loc) => {
                  const className = `${style.filteredLocationItem}`;
                  return (
                    <div
                      key={loc.full_address}
                      className={className}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelect(loc.full_address)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleSelect(loc.full_address);
                        }
                      }}
                    >
                      <span>{loc.full_address}</span>
                    </div>
                  );
                })}
                {suggestions.map((suggestion) => {
                  const className = suggestion.active
                    ? `${style.suggestionItem} ${style.suggestionItemActive}`
                    : `${style.suggestionItem}`;
                  return (
                    <div
                      {...getSuggestionItemProps(suggestion, {
                        className,
                      })}
                    >
                      <span>{suggestion.description}</span>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      )}
    </PlacesAutocomplete>
  );
};

export default LocationSelect;
